import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useMutation } from '@apollo/client';
import { VALIDATE_PARTICIPATION_CODE_MUTATION } from 'Queries/Mutations';
import { QrScannerContext } from 'context/QrScanner';
import { useFormik } from 'formik';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import { useZxing } from 'react-zxing';
import * as yup from 'yup';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { EventMetaData } from 'components/Event/EventMetaData/EventMetaData';
import { InputField } from 'components/FormHelper/InputField';
import { HintText } from 'components/HintText/HintText';
import { HINTTEXT_TYPE } from 'components/HintText/HintTextType';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { OuterBox } from 'components/OuterBox/OuterBox';
import { Separator } from 'components/Separator/Separator';
import { SubTitle } from 'components/SubTitle/SubTitle';

import './EventQrScanner.scss';

const manualInputSchema = yup.object().shape({
  participationCode: yup.string().required('Der Teilnahmecode ist erforderlich.'),
  eventId: yup.string().required('Die Event ID ist erforderlich.'),
});
const MAX_PARSE_ATTEMPTS = 3;
const MANUAL_INPUT_SWITCH_DELAY_MS = 1500;

const parseFailureMessage = (attempt) =>
  `Der gescannte QR Code konnte nicht gelesen werden (Versuch ${attempt} / ${MAX_PARSE_ATTEMPTS}).`;

const hasAlreadyAttendedError = (error) =>
  error?.message?.includes('ALREADY_ATTENDED') ||
  error?.graphQLErrors?.some(({ message }) => message?.includes('ALREADY_ATTENDED')) ||
  error?.message === 'Code already used';

export const EventQrScanner = ({ event }) => {
  const { setQrScannerOpen } = useContext(QrScannerContext);
  const [enableScanner, setEnableScanner] = useState(false);
  const [hintObject, setHintObject] = useState(null);
  const [showManualInput, setShowManualInput] = useState(false);

  const parseAttemptsRef = useRef(0);
  const formikRef = useRef(null);

  // Delay the swap to the manual input form so the user can read the
  // max-attempts error before the UI changes underneath them.
  const switchToManualInput = useMemo(
    () => debounce(() => setShowManualInput(true), MANUAL_INPUT_SWITCH_DELAY_MS),
    []
  );

  useEffect(() => () => switchToManualInput.cancel(), [switchToManualInput]);

  const [validateUserMutation, { loading: isLoading }] = useMutation(
    VALIDATE_PARTICIPATION_CODE_MUTATION,
    {
      onCompleted: (data) => {
        if (!data?.validateAttendance?.success) {
          setHintObject({
            type: HINTTEXT_TYPE.ERROR,
            message: 'Registrierung fehlgeschlagen.',
          });
          formikRef.current?.setSubmitting(false);
          return;
        }

        formikRef.current?.resetForm();
        setHintObject({
          type: HINTTEXT_TYPE.INFO,
          message: 'Registrierung erfolgreich abgeschlossen.',
        });
      },
      onError: (error) => {
        console.error(error);
        formikRef.current?.setSubmitting(false);
        if (hasAlreadyAttendedError(error)) {
          setHintObject({
            type: HINTTEXT_TYPE.ERROR,
            message: 'Die Anwesenheit wurde bereits erfasst.',
          });
          return;
        }
        setHintObject({
          type: HINTTEXT_TYPE.ERROR,
          message: 'Registrierung fehlgeschlagen.',
        });
      },
    }
  );

  const submitMutation = (values) => {
    validateUserMutation({
      variables: {
        participationCode: Number(values.participationCode),
        eventId: Number(values.eventId),
      },
    });
  };
  const formik = useFormik({
    initialValues: {
      participationCode: '',
      eventId: String(event.id ?? ''),
    },
    validationSchema: manualInputSchema,
    onSubmit: (values) => {
      submitMutation(values);
    },
  });
  formikRef.current = formik;

  const resetScannerAndForm = () => {
    formik.resetForm();
    setHintObject(null);
    parseAttemptsRef.current = 0;
    switchToManualInput.cancel();
  };
  const { ref } = useZxing({
    paused: !enableScanner,
    timeBetweenDecodingAttempts: 500,
    onError: (error) => {
      console.error(error);
      setHintObject({
        type: HINTTEXT_TYPE.ERROR,
        message: 'Kamera konnte nicht gestartet werden. Bitte prüfen Sie die Berechtigungen.',
      });
    },
    onDecodeResult: (decodedResult) => {
      try {
        const parsed = JSON.parse(decodedResult.getText());
        parseAttemptsRef.current = 0;
        switchToManualInput.cancel();
        setEnableScanner(false);
        submitMutation(parsed);
      } catch (error) {
        console.error(error);
        parseAttemptsRef.current += 1;

        if (parseAttemptsRef.current >= MAX_PARSE_ATTEMPTS) {
          setEnableScanner(false);
          setHintObject({
            type: HINTTEXT_TYPE.ERROR,
            message: `${parseFailureMessage(
              parseAttemptsRef.current
            )} Bitte geben Sie die Daten manuell ein.`,
          });
          parseAttemptsRef.current = 0;
          switchToManualInput();
          return;
        }

        setHintObject({
          type: HINTTEXT_TYPE.ERROR,
          message: parseFailureMessage(parseAttemptsRef.current),
        });
      }
    },
  });

  return (
    <OuterBox>
      <EventMetaData event={event} />
      <SubTitle text="QR Code scannen" notFramed />
      <Separator />
      <div className="event-qr-scanner">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          playsInline
          muted
          ref={ref}
          aria-label="Qr code scanner"
          className={enableScanner ? 'event-qr-scanner__video' : 'event-qr-scanner__video-hidden'}
        />
        {showManualInput && (
          <form className="event-qr-scanner__manual-input" onSubmit={formik.handleSubmit}>
            <InputField
              label="Teilnahmecode"
              name="participationCode"
              value={formik.values.participationCode}
              onChange={formik.handleChange}
              fullWidth
              error={formik.touched.participationCode && Boolean(formik.errors.participationCode)}
              helperText={formik.touched.participationCode && formik.errors.participationCode}
            />
            <div>
              <ActionButton type="submit" title="Validieren" />
            </div>
          </form>
        )}
        {isLoading && <LoadingIndicator />}
        <div className="event-qr-scanner__hint-container">
          {hintObject && <HintText type={hintObject.type}>{hintObject.message}</HintText>}
          {!isLoading && (
            <HintText type={HINTTEXT_TYPE.INFO}>
              {enableScanner
                ? `Deaktivieren Sie den Scanner, wenn er nicht genutzt wird, um übermäßigen Akkuverbrauch zu vermeiden.`
                : `Aktivieren des Scanners setzt den aktuellen Status zurück und ermöglicht das erneute Scannen. Bitte beachten Sie, dass das Aktivieren der Kamera ein paar Sekunden dauern kann.`}
            </HintText>
          )}
        </div>

        <div className="event-qr-scanner__buttons-container">
          <ActionButton
            title={enableScanner ? 'Scanner deaktivieren' : 'Scanner aktivieren'}
            onClick={() => {
              setEnableScanner((prev) => !prev);
              setShowManualInput(false);
              resetScannerAndForm();
            }}
            inverted={enableScanner}
            disabled={isLoading || formik.isSubmitting}
          />

          <ActionButton
            title="Manuelle Eingabe"
            disabled={isLoading || formik.isSubmitting}
            onClick={() => {
              setShowManualInput((prev) => !prev);
              setEnableScanner(false);
              resetScannerAndForm();
            }}
            inverted={showManualInput}
          />
        </div>
        <div className="event-qr-scanner__close-button-container">
          <ActionButton
            onClick={() => setQrScannerOpen(false)}
            title="Schließen"
            disabled={isLoading || formik.isSubmitting}
          />
        </div>
      </div>
    </OuterBox>
  );
};

EventQrScanner.propTypes = {
  event: PropTypes.object.isRequired,
};
